import {
    Autocomplete,
    Box,
    debounce,
    FormControl,
    FormHelperText,
    IconButton,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import { ReactElement, useEffect, useMemo } from 'react'
import TruncatedText from './TruncatedText'
import { AddRounded } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

export interface SelectItem {
    [key: string]: any
}

interface SearchInAPIAndSelectInputProps {
    id: string
    label: string
    value: SelectItem | null
    placeholder?: string
    onSelect: (id: string, value: SelectItem | null) => void
    onSearch: (id: string, text: string) => void
    list: SelectItem[]
    required?: boolean
    disabled?: boolean
    isLegend?: boolean
    list_Height?: string
    hasAddNew?: boolean
    onClickAddNew?: () => void
    onClickRowIcon?: (item: SelectItem) => void
    hasEmpty?: boolean
    errorMessage?: string
    onFetchNextPage?: () => void
    hasMore?: boolean
    isFetching?: boolean
    itemId?: string
    itemName?: string
    hasItemsEdit?: boolean
    allowClear?: boolean
    fieldSize?: 'medium' | 'small'
    getDisplayLabel?: (option: SelectItem) => string
}

const SearchInAPIAndSelectInput = ({
    id,
    label,
    value = {
        id: '',
        name: '',
    },
    placeholder,
    onSelect,
    onSearch,
    list = [],
    required = false,
    disabled = false,
    isLegend = false,
    list_Height = '250px',
    hasAddNew = false,
    onClickAddNew = () => {},
    onClickRowIcon,
    hasEmpty = true,
    errorMessage = '',
    onFetchNextPage,
    hasMore = false,
    isFetching = false,
    itemId = 'id',
    itemName = 'name',
    hasItemsEdit,
    allowClear = true,
    fieldSize = 'small',
    getDisplayLabel,
}: SearchInAPIAndSelectInputProps): ReactElement => {
    const { t } = useTranslation()
    const addNewOption = {
        [itemId]: 'ADD_NEW',
        [itemName]: '',
        hasItemEdit: false,
    }
    const fullList = useMemo(() => {
        const baseList = [...(list || [])]
        if (hasEmpty) {
            baseList.unshift({
                [itemId]: '',
                [itemName]: '',
                hasItemEdit: false,
            })
        }
        if (hasAddNew) {
            baseList.unshift(addNewOption)
        }

        return baseList
    }, [list, hasEmpty, hasAddNew, itemId, itemName])

    const debouncedSreach = useMemo(
        () =>
            debounce((text: string) => {
                onSearch(id, text)
            }, 300),
        [id, onSearch],
    )

    const handleInputChange = (
        _event: any,
        newInputValue: string,
        reason: string,
    ) => {
        if (
            reason === 'reset' ||
            reason === 'blur' ||
            reason === 'selectOption'
        ) {
            debouncedSreach('')
        } else {
            debouncedSreach(newInputValue)
        }
    }

    const handleChange = (_event: any, newValue: SelectItem | null) => {
        if (newValue?.id || newValue?.[itemId]) {
            onSelect(id, newValue)
        } else {
            onSelect(id, null)
        }
    }

    const handleRowIcon = (e: any, item: any) => {
        e.stopPropagation()
        onClickRowIcon?.(item)
    }

    useEffect(() => {
        return () => {
            debouncedSreach.clear()
        }
    }, [debouncedSreach])
    return (
        <Box sx={{ width: '100%' }}>
            <FormControl fullWidth disabled={disabled} size={fieldSize}>
                {!isLegend && (
                    <Typography sx={{ opacity: 0.7, fontSize: 14 }}>
                        {label}
                        {required && '*'}
                    </Typography>
                )}

                <Autocomplete
                    id={`${id}-select`}
                    options={fullList}
                    value={value}
                    getOptionLabel={(option) =>
                        option?.[itemId] === 'ADD_NEW'
                            ? t('addNew')
                            : getDisplayLabel
                              ? getDisplayLabel(option)
                              : option[itemName] || ''
                    }
                    onInputChange={handleInputChange}
                    onChange={handleChange}
                    isOptionEqualToValue={(opt, val) =>
                        opt[itemId] === val?.[itemId]
                    }
                    filterOptions={(options) => options}
                    disableClearable={!allowClear || !value?.[itemId]}
                    disabled={disabled}
                    loading={isFetching}
                    slotProps={{
                        clearIndicator: {
                            id: `${id}-clear-button`,
                        },
                        popupIndicator: {
                            id: `${id}-dropdown-button`,
                        },
                    }}
                    ListboxProps={{
                        style: { maxHeight: list_Height },
                        onScroll: (event: React.UIEvent<HTMLElement>) => {
                            const listboxNode = event.currentTarget
                            const bottomReached =
                                listboxNode.scrollTop +
                                    listboxNode.clientHeight >=
                                listboxNode.scrollHeight - 10

                            if (bottomReached && hasMore && !isFetching) {
                                onFetchNextPage?.()
                            }
                        },
                    }}
                    loadingText={t('loading')}
                    noOptionsText={isFetching ? t('loading') : t('noOptions')}
                    renderOption={(props, option) =>
                        option?.[itemId] === 'ADD_NEW' ? (
                            <Box
                                component="li"
                                id={id + 'add_new'}
                                key="add_new"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: '100%',
                                    cursor: 'pointer',
                                    padding: '2px 10px',
                                    '& :hover': {
                                        background: '#f9f9f9',
                                    },
                                }}
                                onClick={() => onClickAddNew()}
                            >
                                <AddRounded color={'primary'} />
                                <Box
                                    sx={{
                                        marginLeft: '5px',
                                    }}
                                >
                                    <Typography
                                        fontWeight="600"
                                        fontSize="14px"
                                    >
                                        {t('addNew')}
                                    </Typography>
                                </Box>
                            </Box>
                        ) : (
                            <Box
                                component="li"
                                {...props}
                                key={`${option?.[itemId]}`}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    '&:hover': {
                                        '& > .row-icon': {
                                            display: 'flex',
                                        },
                                    },
                                }}
                            >
                                <TruncatedText
                                    text={
                                        option?.[itemId] === ''
                                            ? t('selectPlaceholder')
                                            : getDisplayLabel
                                              ? getDisplayLabel(option)
                                              : option?.[itemName]
                                    }
                                    sx={{
                                        fontSize: 14,
                                        opacity:
                                            option?.[itemId] === '' ? 0.4 : 1,
                                    }}
                                />
                                {hasItemsEdit === false
                                    ? null
                                    : (option.hasItemEdit || hasItemsEdit) &&
                                      option?.[itemId] !== '' && (
                                          <IconButton
                                              id={
                                                  id +
                                                  '-' +
                                                  option?.[itemName] +
                                                  '-edit'
                                              }
                                              onClick={(e) =>
                                                  handleRowIcon(e, option)
                                              }
                                              aria-controls="simple-menu"
                                              aria-haspopup="true"
                                              key={`${option?.[itemId]}-edit`}
                                              disabled={false}
                                              sx={{
                                                  padding: '0px',
                                                  marginLeft: 'auto',
                                                  display: 'none',
                                              }}
                                              className="row-icon"
                                          >
                                              <img
                                                  src="/assets/images/icons/edit.svg"
                                                  alt="Edit"
                                              />
                                          </IconButton>
                                      )}
                            </Box>
                        )
                    }
                    renderInput={(params) => (
                        <Tooltip
                            title={
                                value && getDisplayLabel
                                    ? getDisplayLabel(value)
                                    : value?.[itemName] || ''
                            }
                            arrow
                            placement="top"
                        >
                            <TextField
                                {...params}
                                label={
                                    isLegend
                                        ? `${label}${required ? ' *' : ''}`
                                        : ''
                                }
                                size={fieldSize}
                                placeholder={
                                    (!value || value?.[itemId] === '') &&
                                    placeholder
                                        ? placeholder
                                        : t('selectLabel', { label })
                                }
                                error={!!errorMessage}
                                InputProps={{
                                    ...params.InputProps,
                                    style: {
                                        fontSize: 14,
                                        boxShadow: 'none',
                                    },
                                }}
                            />
                        </Tooltip>
                    )}
                    sx={{
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                            padding: `${fieldSize === 'small' ? '2.5px 4px 2.5px 5px' : ''}`,
                            '& input::placeholder': {
                                opacity: 0.2,
                            },
                        },
                        '& > div > div': {
                            paddingRight: '55px !important',
                        },
                    }}
                />

                {errorMessage && (
                    <FormHelperText sx={{ color: 'red' }}>
                        {errorMessage}
                    </FormHelperText>
                )}
            </FormControl>
        </Box>
    )
}

export default SearchInAPIAndSelectInput
